import React, { useEffect, useState, useRef } from "react";
import { Form, Spin, message, Modal, Select, Tooltip } from "antd";
import { FiFileText } from "react-icons/fi";
import { BiDollarCircle } from "react-icons/bi";
import { MdHistory } from "react-icons/md";
import { TbClipboardList } from "react-icons/tb";
import { FaUserDoctor } from "react-icons/fa6";
import { FcSettings } from "react-icons/fc";

import drEasyApi from "../../../Api/Customapi";
import { calculateAge, formatTo12Hour } from "../../../utils/helpers";

import GeneralNotesTab from "./Tabs/GeneralNotesTab";
import FinancialNotesTab from "./Tabs/FinancialNotesTab";
import SessionsTab from "./Tabs/SessionsTab";
import AssessmentTab from "./Tabs/AssessmentTab";
import Assessment2Tab from "./Tabs/Assesment2Tab";
import FinancialReportModal from "./FinanicalReportModal";
import { IoSettingsOutline } from "react-icons/io5";

const SESSION_COUNT = 7;

const REPORT_TYPE_INT = { General: 0, PhysioTherapy: 1, Lymphedema: 2 };
const REPORT_TYPE_STRING = { 0: "General", 1: "PhysioTherapy", 2: "Lymphedema" };

const toReportTypeString = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return REPORT_TYPE_STRING[value] ?? null;
  if (typeof value === "string" && value in REPORT_TYPE_INT) return value;
  return null;
};

const toReportTypeInt = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  return REPORT_TYPE_INT[value] ?? 0;
};

const emptyAssessment = () => ({
  diagnosis: "", pastHistory: "", chiefComplain: "", jobOccupation: "",
  weeklyDays: [],
  sessions: Array.from({ length: SESSION_COUNT }, (_, i) => ({
    no: i + 1, date: "", day: "", physiotherapist: "", patientSignature: "", notes: "",
  })),
  painScore: "", painDistribution: "", lymphaticEdema: "",
  radioInvestigations: "", labNotes: "", labResults: "",
  precautions: "", ptAssessment: "", ptPlanOfTreatment: "",
  sessionsNum: "", progressNotes: "", tttGoals: "", discharge: "", anyOtherNotes: "",
});

const emptyAssessmentApiState = () => ({
  diagnosis: "", pastHistory: "", chiefComplain: "", jobAndOccupation: "",
  painScore: 0, painDistribution: "", lymphaticEdema: false,
  radioInvestigations: "", labNotes: false, labResults: "",
  precautions: "", ptAssessment: "", ptPlanOfTreatment: "",
  sessionsNum: 0, progressNotes: "", tttGoals: "", discharge: "",
  otherNotes: "", operations: "", presentHistory: "", skinAbnormalities: "",
  treatmentMLD: false, treatmentPCD: false, treatmentTECAR: false,
  treatmentBandaging: false, treatmentExercises: false, treatmentOther: "",
});

const TAB_VISIBILITY = {
  General: ["notes", "order", "sessions"],
  PhysioTherapy: ["assessment", "sessions", "order"],
  Lymphedema: ["lymphedema", "sessions", "order"],
};

const ALL_TABS = [
  { id: "notes", label: "General Notes", icon: FiFileText },
  { id: "assessment", label: "PhysioTherapy Assessment", icon: TbClipboardList },
  { id: "lymphedema", label: "Lymphedema Assessment", icon: TbClipboardList },
];

const REPORT_TYPE_OPTIONS = [
  { value: 0, label: "General", description: "Standard patient notes and general documentation" },
  { value: 1, label: "PhysioTherapy", description: "Physiotherapy assessment, treatment plans and progress notes" },
  { value: 2, label: "Lymphedema", description: "Lymphedema-specific assessment and treatment tracking" },
];

const buildAssessmentApiState = (report) => ({
  diagnosis: report.diagnosis || "",
  pastHistory: report.pastHistory || "",
  chiefComplain: report.chiefComplain || "",
  jobAndOccupation: report.jobAndOccupation || "",
  painScore: report.painScore ?? 0,
  painDistribution: report.painDistribution || "",
  lymphaticEdema: report.lymphaticEdema ?? false,
  radioInvestigations: report.radioInvestigations || "",
  labNotes: report.labNotes ?? false,
  labResults: report.labResults || "",
  precautions: report.precautions || "",
  ptAssessment: report.ptAssessment || "",
  ptPlanOfTreatment: report.ptPlanOfTreatment || "",
  sessionsNum: report.sessionsNum ?? 0,
  progressNotes: report.progressNotes || "",
  tttGoals: report.tttGoals || "",
  discharge: report.discharge || "",
  otherNotes: report.otherNotes || "",
  operations: report.operations || "",
  presentHistory: report.presentHistory || "",
  skinAbnormalities: report.skinAbnormalities || "",
  treatmentMLD: report.treatmentMLD ?? false,
  treatmentPCD: report.treatmentPCD ?? false,
  treatmentTECAR: report.treatmentTECAR ?? false,
  treatmentBandaging: report.treatmentBandaging ?? false,
  treatmentExercises: report.treatmentExercises ?? false,
  treatmentOther: report.treatmentOther || "",
});

const hydrateFromReport = (report, { setFreeFormNotes, setSavedSections, setAssessmentApiState }) => {
  setFreeFormNotes(report.body || "");
  if (report.reportSections?.length > 0) {
    setSavedSections(report.reportSections.map((s) => ({
      sectionId: s.id,
      title: s.title || "",
      content: s.body || "",
      photoIDs: s.photoIds || [],
      createdAt: s.createdAt,
    })));
  } else {
    setSavedSections([]);
  }
  setAssessmentApiState(buildAssessmentApiState(report));
};

// ── Adapts a PatientDetails `patient` object into the ActivePatient shape ──
// PatientDetails passes a `patient` prop with shape: { id, accountId, firstName, lastName, ... }
// ActivePatient API returns patientData with shape: { patient: {...}, bookingId, bookingDate, ... }
// When no booking context is available (PatientDetails mode), we create a minimal wrapper.
const adaptPatientProp = (patient) => ({
  patient: {
    userID: patient.accountId ?? patient.userID ?? patient.id,
    firstName: patient.firstName,
    lastName: patient.lastName,
    email: patient.email,
    gender: patient.gender,
    birthDay: patient.birthDay ?? patient.dateOfBirth,
    phoneNumber: patient.phoneNumber,
    profileId: patient.profileId ?? patient.id,
  },
  centerID: patient.centerID ?? null,
  bookingId: null,
  bookingDate: null,
  startTime: null,
  endTime: null,
  completedSessions: patient.completedSessions ?? [],
  reportDefultType: patient.reportDefultType ?? null,
  // flag so we know we're in "details" mode (no booking context)
  _isPatientDetailsMode: true,
});

function SettingsDropdown({ onViewSessions, onAddFinancialReport, hideFinancial }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        aria-label="Settings"
      >
        <IoSettingsOutline size={24} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-10 z-50 w-56 rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden"
          style={{ boxShadow: "0 8px 24px rgba(41,141,159,0.18)" }}
        >
          <button
            onClick={() => { setOpen(false); onViewSessions(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary/10 hover:text-primary transition-colors font-medium"
          >
            <MdHistory size={18} className="text-primary" />
            See Patient Sessions
          </button>
          {!hideFinancial && (
            <>
              <div className="h-px bg-gray-100" />
              <button
                onClick={() => { setOpen(false); onAddFinancialReport(); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary/10 hover:text-primary transition-colors font-medium"
              >
                <BiDollarCircle size={18} className="text-primary" />
                Add Financial Report
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SessionsModalContent({ completedSessions }) {
  if (!completedSessions?.length) {
    return (
      <p className="text-gray-500 text-center py-8">No completed sessions found</p>
    );
  }

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      {completedSessions.map((session) => (
        <div
          key={session.sessionId}
          className="rounded-lg p-4 bg-white shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 bg-primary/10 mb-3 p-3 rounded-md">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <FaUserDoctor /> Specialist:
            </p>
            <p className="font-semibold text-primary">
              {session.specialistName || "N/A"}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-primary/5 p-2 rounded-md">
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-semibold text-primary">
                {new Date(session.sessionDate).toLocaleDateString()}
              </p>
            </div>
            <div className="bg-primary/5 p-2 rounded-md">
              <p className="text-sm text-gray-600">Start Time</p>
              <p className="font-semibold text-primary">
                {formatTo12Hour(session.startTime)}
              </p>
            </div>
          </div>
          {session.notes && (
            <p className="mt-3 text-gray-700 text-sm bg-gray-50 p-3 rounded-lg border border-gray-200">
              Note: {session.notes}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Props:
//   patient (optional) — when passed, skips the ActivePatient API fetch and
//                        uses this patient's data instead (PatientDetails mode).
//                        Shape: the patient object from PatientDetails/PatientsList.
// ─────────────────────────────────────────────────────────────────────────────
export default function CurrentActivePatient({ patient: patientProp }) {
  const [activeTab, setActiveTab] = useState(null);

  // ── shared report state ──
  const [filteredReports, setFilteredReports] = useState([]);
  const [currentReportIndex, setCurrentReportIndex] = useState(0);
  const [reportFound, setReportFound] = useState(false);
  const [freeFormNotes, setFreeFormNotes] = useState("");
  const [savedSections, setSavedSections] = useState([]);
  const [newSection, setNewSection] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  const [seeMore, setSeeMore] = useState(false);

  // ── financial tab state ──
  const [orderNotes, setOrderNotes] = useState("");
  const [doctorServices, setDoctorServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState({});
  const [servicesLoading, setServicesLoading] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [existingOrderReport, setExistingOrderReport] = useState(null);
  const [loadingOrderReport, setLoadingOrderReport] = useState(false);

  // ── assessment tab state ──
  const [assessment, setAssessment] = useState(emptyAssessment());
  const [assessmentApiState, setAssessmentApiState] = useState(emptyAssessmentApiState());
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [assessmentForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [reportType, setReportType] = useState("General");
  const [loadingReport, setLoadingReport] = useState(false)

  // ── patient data ──
  const [patientData, setPatientData] = useState(
    // If a patient prop was provided, pre-populate immediately (no loading flash)
    patientProp ? adaptPatientProp(patientProp) : null
  );
  const [loading, setLoading] = useState(!patientProp); // skip loading if prop provided
 

  // ── create / change report modal ──
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [creatingReport, setCreatingReport] = useState(false);

  // ── settings modals ──
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showFinancialModal, setShowFinancialModal] = useState(false);

  // ─── Derived values ───────────────────────────────────────────────────────
  const currentReport = filteredReports[currentReportIndex] ?? null;
  const reportId = currentReport?.id ?? null;
  const physioTherapyData = currentReport?.physioTherapyData ?? null;
  const lymphedemaData = currentReport?.lymphedemaData ?? null;

  // true when rendered inside PatientDetails (no booking context)
  const isPatientDetailsMode = !!patientProp;

  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (patientProp) {
      // Prop-driven mode: adapt the prop and fetch report directly
      const adapted = adaptPatientProp(patientProp);
      setPatientData(adapted);
      const defaultType = toReportTypeString(patientProp.reportDefultType) ?? "General";
      setReportType(defaultType);
      if (adapted.patient?.userID) {
        fetchUserReport(adapted.patient.userID, defaultType);
      }
    } else {
      // Standalone mode: fetch active patient from API
      fetchActivePatient();
    }
  }, []);

  useEffect(() => {
    if (!patientProp) return;

    const adapted = adaptPatientProp(patientProp);
    setPatientData(adapted);

    const defaultType =
      toReportTypeString(patientProp.reportDefultType) ?? "General";

    setReportType(defaultType);

    if (adapted.patient?.userID) {
      fetchUserReport(adapted.patient.userID, defaultType);
    }
  }, [patientProp]);

  useEffect(() => {
    if (!patientData) return;
    if (patientData.centerID) fetchServices();
    // Only fetch order report when there's a real bookingId (not in PatientDetails mode)
    if (patientData.bookingId) fetchOrderReport();
  }, [patientData]);

  useEffect(() => {
    if (!reportFound) { setActiveTab("order"); return; }
    if (!reportType) return;
    const visibleIds = TAB_VISIBILITY[reportType] ?? ["order", "sessions"];
    setActiveTab(visibleIds[0]);
  }, [reportType, reportFound]);

  // ─────────────────────────────────────────────────────────────────────────
  const fetchActivePatient = async () => {
    setLoading(true);
    try {
      const res = await drEasyApi.get("/api/Doctor/ActivePatient");
      setPatientData(res.data);
      setReportType(toReportTypeString(res.data.reportDefultType) ?? "General");
      if (res.data.patient?.userID) {
        await fetchUserReport(res.data.patient.userID, reportType);
      }
    } catch (err) {
      if (err.response?.status === 403) window.location.href = "/sub";
      else if (err.response?.status === 404) message.info("No active patient at the moment");
      else message.error("Failed to load active patient");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserReport = async (userId, reportTypeValue) => {
    const reportTypeInt = toReportTypeInt(reportTypeValue);
    const typeStr = toReportTypeString(reportTypeValue);
    try {
      setLoadingReport(true)
      const res = await drEasyApi.get("/api/Reports/GetUserReport", {
        params: { userID: userId, ReportType: reportTypeInt },
      });
      const reports = Array.isArray(res.data) ? res.data : [res.data];
      const matched = reports.filter((r) => toReportTypeString(r.reportType) === typeStr);
      if (matched.length > 0) {
        setFilteredReports(matched);
        setCurrentReportIndex(0);
        setReportFound(true);
        const first = matched[0];
        const resolvedType = toReportTypeString(first.defaultReportType) ?? typeStr;
        setReportType(resolvedType);
        hydrateFromReport(first, { setFreeFormNotes, setSavedSections, setAssessmentApiState });
      } else {
        setFilteredReports([]);
        setCurrentReportIndex(0);
        setReportFound(false);
        if (typeStr) setReportType(typeStr);
      }
    } catch {
      setFilteredReports([]);
      setCurrentReportIndex(0);
      setReportFound(false);
    } finally {
      setLoadingReport(false)
    }
  };

  const fetchUserReportAndRestoreId = async (preserveReportId) => {
    const reportTypeValue = reportType;
    const reportTypeInt = toReportTypeInt(reportTypeValue);
    const typeStr = toReportTypeString(reportTypeValue);
    try {
      const res = await drEasyApi.get("/api/Reports/GetUserReport", {
        params: { userID: patientData.patient.userID, ReportType: reportTypeInt },
      });
      const reports = Array.isArray(res.data) ? res.data : [res.data];
      const matched = reports.filter((r) => toReportTypeString(r.reportType) === typeStr);
      if (matched.length > 0) {
        setFilteredReports(matched);
        setReportFound(true);
        const restoredIndex = preserveReportId
          ? Math.max(matched.findIndex((r) => r.id === preserveReportId), 0)
          : currentReportIndex;
        setCurrentReportIndex(restoredIndex);
        hydrateFromReport(matched[restoredIndex], { setFreeFormNotes, setSavedSections, setAssessmentApiState });
      }
    } catch { }
  };

  const handleReportIndexChange = (newIndex) => {
    if (newIndex < 0 || newIndex >= filteredReports.length) return;
    const report = filteredReports[newIndex];
    setCurrentReportIndex(newIndex);
    hydrateFromReport(report, { setFreeFormNotes, setSavedSections, setAssessmentApiState });
  };

  const handleReportTypeChange = async (value) => {
    setReportType(value);
    setReportFound(false);
    setFilteredReports([]);
    setCurrentReportIndex(0);
    if (patientData?.patient?.userID) {
      await fetchUserReport(patientData.patient.userID, value);
    }
  };

  const fetchServices = async () => {
    setServicesLoading(true);
    try {
      const res = await drEasyApi.get(`/api/Services/GetCenterServices?CenterId=${patientData.centerID}`);
      setDoctorServices(res.data);
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchOrderReport = async () => {
    setLoadingOrderReport(true);
    try {
      const res = await drEasyApi.get(`/api/Reports/OrderReport`, { params: { bookingId: patientData.bookingId } });
      if (res.data) {
        setExistingOrderReport(res.data);
        setOrderNotes(res.data.notes || "");
      }
    } catch {
      setExistingOrderReport(null);
    } finally {
      setLoadingOrderReport(false);
    }
  };

  const handleCreateReport = async () => {
    if (selectedReportType === null) { message.warning("Please select a report type"); return; }
    setCreatingReport(true);
    try {
      await drEasyApi.post("/api/Reports/CraeteReport", {
        userId: patientData.patient.userID,
        reportType: selectedReportType,
      });
      message.success("Report created successfully");
      setShowCreateModal(false);
      setSelectedReportType(null);
      await fetchUserReport(patientData.patient.userID, selectedReportType);
    } catch (err) {
      message.error("Failed to create report");
      console.error(err);
    } finally {
      setCreatingReport(false);
    }
  };

  const handleSaveReport = async (extra = {}) => {
    await drEasyApi.put("/api/Reports/EditReport", {
      reportId,
      body: freeFormNotes,
      photoIDs: [],
      ...assessmentApiState,
      ...extra,
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen flex justify-center items-center">
      <Spin size="large" />
    </div>
  );

  if (!patientData) return (
    <div className="min-h-screen flex justify-center items-center">
      <p className="text-xl text-gray-600">No active patient at the moment</p>
    </div>
  );

  const patient = patientData.patient;
  const FALLBACK_TAB_IDS = ["order", "sessions"];
  const visibleTabIds = reportFound
    ? (TAB_VISIBILITY[reportType] ?? FALLBACK_TAB_IDS)
    : FALLBACK_TAB_IDS;
  const tabs = ALL_TABS.filter((t) => visibleTabIds.includes(t.id));

  return (
    <div className={isPatientDetailsMode ? "" : "min-h-screen"}>
      <div className="mx-auto">

        {/* ── Patient Header: only shown in standalone mode ── */}
        {!isPatientDetailsMode && (
          <div
            className="bg-white rounded-lg p-6 mb-6"
            style={{ boxShadow: "0 6px 14px rgba(41, 141, 159, 0.3)" }}
          >
            <div className="flex items-start justify-center mb-4 relative">
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {patient.firstName} {patient.lastName}
                </h1>
                <p className="text-gray-600">Patient ID: {patient.profileId}</p>
              </div>

              <div className="absolute right-0 top-0">
                <SettingsDropdown
                  onViewSessions={() => setShowSessionsModal(true)}
                  onAddFinancialReport={() => setShowFinancialModal(true)}
                  hideFinancial={false}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {[
                { label: "Email", value: patient.email },
                { label: "Gender", value: patient.gender },
                { label: "Age", value: calculateAge(patient.birthDay) },
                { label: "Phone", value: patient.phoneNumber },
                { label: "Booking Date", value: patientData.bookingDate ? new Date(patientData.bookingDate).toLocaleDateString() : "N/A" },
                { label: "Time Slot", value: patientData.startTime ? `${formatTo12Hour(patientData.startTime)} – ${formatTo12Hour(patientData.endTime)}` : "N/A" },
              ].map(({ label, value }) => (
                <div key={label} className="bg-primary/10 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className="text-base font-semibold text-primary">{value || "N/A"}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── In PatientDetails mode: show a slim settings bar ── */}
        {isPatientDetailsMode && (
          <div className="flex justify-end mb-3">
            <SettingsDropdown
              onViewSessions={() => setShowSessionsModal(true)}
              onAddFinancialReport={() => setShowFinancialModal(true)}
              // No booking → hide financial report option
              hideFinancial={!patientData.bookingId}
            />
          </div>
        )}

        {/* No report banner */}
        {!reportFound && (
          <div className="mb-6 flex flex-col items-center justify-center gap-4 rounded-2xl border-primary/30 bg-primary/5 py-10 px-6 text-center">
            <div>
              <p className="text-lg font-bold text-gray-800">No {reportType || "Report"} Report created yet</p>
              <p className="mt-1 text-sm text-gray-500">
                Create a report first to unlock the assessment and notes tabs for this patient.
              </p>
            </div>
            <button
              onClick={() => { setSelectedReportType(null); setShowCreateModal(true); }}
              className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 font-semibold text-white hover:bg-primary/90 transition-colors"
            >
              <span className="text-lg leading-none">＋</span>
              Create Report
            </button>
          </div>
        )}

        {/* ── Tabs + Content ── */}
        <div
          className="bg-white rounded-lg p-2 md:p-6"
          style={{ boxShadow: "0 6px 14px rgba(41, 141, 159, 0.3)" }}
        >
          {/* Tab bar */}
          <div className="flex border-b-2 border-gray-200 mb-6 flex-wrap gap-y-1 items-center justify-between">
            <div className="flex flex-wrap gap-y-1">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all relative ${activeTab === id ? "text-primary" : "text-gray-500 hover:text-gray-700"}`}
                >
                  <Icon size={20} />
                  {label}
                  {activeTab === id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 my-1 mr-1">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">Report type:</span>
                <Select
                  style={{ width: 180 }}
                  value={reportType}
                  onChange={handleReportTypeChange}
                  placeholder="Select type"
                >
                  <Select.Option value="General">General</Select.Option>
                  <Select.Option value="PhysioTherapy">PhysioTherapy</Select.Option>
                  <Select.Option value="Lymphedema">Lymphedema</Select.Option>
                </Select>
              </div>

              <Tooltip title="Create a new report for this patient">
                <button
                  onClick={() => { setSelectedReportType(null); setShowCreateModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-white transition-colors text-sm"
                >
                  <span className="text-lg leading-none">＋</span>
                  <span className="hidden md:flex"> Create Report</span>
                </button>
              </Tooltip>
            </div>
          </div>

          {/* ── Tab content ── */}
          <div className="min-h-[400px]">
            {loadingReport ? (
              <div className="flex justify-center items-center h-[400px]">
                <Spin size="large" />
              </div>
            ) : (<>
              {activeTab === "notes" && reportFound && (

                <GeneralNotesTab
                  reportId={reportId}
                  freeFormNotes={freeFormNotes} setFreeFormNotes={setFreeFormNotes}
                  savedSections={savedSections} setSavedSections={setSavedSections}
                  newSection={newSection} setNewSection={setNewSection}
                  editingSectionId={editingSectionId} setEditingSectionId={setEditingSectionId}
                  savingNotes={savingNotes} setSavingNotes={setSavingNotes}
                  savingSection={savingSection} setSavingSection={setSavingSection}
                  seeMore={seeMore} setSeeMore={setSeeMore}
                  onSaveNotes={handleSaveReport}
                  reportCount={filteredReports.length}
                  currentReportIndex={currentReportIndex}
                  onReportIndexChange={handleReportIndexChange}
                  fetchUserReport={() => fetchUserReportAndRestoreId(reportId)}
                />
              )}

              {activeTab === "assessment" && reportFound && (
                <AssessmentTab
                  patientData={patientData}
                  assessmentApiState={assessmentApiState}
                  setAssessmentApiState={setAssessmentApiState}
                  assessment={assessment} setAssessment={setAssessment}
                  savingAssessment={savingAssessment} setSavingAssessment={setSavingAssessment}
                  assessmentForm={assessmentForm} scheduleForm={scheduleForm}
                  onSave={handleSaveReport}
                  physioTherapyData={physioTherapyData}
                  reportCount={filteredReports.length}
                  currentReportIndex={currentReportIndex}
                  onReportIndexChange={handleReportIndexChange}
                  fetchUserReport={() => fetchUserReportAndRestoreId(reportId)}
                />
              )}

              {activeTab === "lymphedema" && reportFound && (
                <Assessment2Tab
                  patientData={patientData}
                  assessmentApiState={assessmentApiState}
                  setAssessmentApiState={setAssessmentApiState}
                  assessment={assessment} setAssessment={setAssessment}
                  savingAssessment={savingAssessment} setSavingAssessment={setSavingAssessment}
                  assessmentForm={assessmentForm} scheduleForm={scheduleForm}
                  onSave={handleSaveReport}
                  physioTherapyData={physioTherapyData}
                  lymphedemaData={lymphedemaData}
                  reportId={reportId}
                  fetchUserReport={() => fetchUserReportAndRestoreId(reportId)}
                  reportCount={filteredReports.length}
                  currentReportIndex={currentReportIndex}
                  onReportIndexChange={handleReportIndexChange}
                />
              )}
            </>
            )}
          </div>
        </div>
      </div>

      {/* ── Create Report Modal ── */}
      <Modal
        open={showCreateModal}
        onCancel={() => { if (reportFound) setShowCreateModal(false); }}
        footer={null}
        closable={reportFound}
        maskClosable={reportFound}
        width={560}
        centered
        title={
          <div className="text-xl font-bold text-gray-800 pb-2">
            {reportFound ? "Create New Report" : "Create Patient Report"}
          </div>
        }
      >
        <p className="text-gray-500 mb-6 text-sm">
          Select the report type that best matches this patient's treatment plan.
          {!reportFound && " This cannot be skipped — a report is required to continue."}
        </p>
        <div className="flex flex-col gap-3">
          {REPORT_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSelectedReportType(opt.value)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedReportType === opt.value
                ? "border-primary bg-primary/5"
                : "border-gray-200 hover:border-primary/40 hover:bg-gray-50"
                }`}
            >
              <div className="flex items-center gap-3">
                <div>
                  <p className={`font-semibold ${selectedReportType === opt.value ? "text-primary" : "text-gray-800"}`}>
                    {opt.label}
                  </p>
                  <p className="text-sm text-gray-500">{opt.description}</p>
                </div>
                {selectedReportType === opt.value && (
                  <span className="ml-auto text-primary text-xl">✓</span>
                )}
              </div>
            </button>
          ))}
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          {reportFound && (
            <button
              onClick={() => setShowCreateModal(false)}
              className="px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleCreateReport}
            disabled={selectedReportType === null || creatingReport}
            className={`px-6 py-2.5 rounded-lg font-semibold transition-colors ${selectedReportType === null || creatingReport
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-primary text-white hover:bg-primary/90"
              }`}
          >
            {creatingReport ? "Creating…" : "Create Report"}
          </button>
        </div>
      </Modal>

      {/* ── Sessions Modal ── */}
      <Modal
        open={showSessionsModal}
        onCancel={() => setShowSessionsModal(false)}
        footer={null}
        width={600}
        centered
        title={
          <div className="flex items-center gap-2 text-xl font-bold text-gray-800 pb-2">
            <MdHistory className="text-primary" size={22} />
            Patient Sessions
          </div>
        }
      >
        <SessionsModalContent completedSessions={patientData.completedSessions} />
      </Modal>

      {/* ── Financial Report Modal ── */}
      {!isPatientDetailsMode && (
        <FinancialReportModal
          open={showFinancialModal}
          onClose={() => setShowFinancialModal(false)}
          patientData={patientData}
          orderNotes={orderNotes}
          setOrderNotes={setOrderNotes}
          doctorServices={doctorServices}
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          servicesLoading={servicesLoading}
          submittingOrder={submittingOrder}
          setSubmittingOrder={setSubmittingOrder}
          existingOrderReport={existingOrderReport}
          loadingOrderReport={loadingOrderReport}
          fetchOrderReport={fetchOrderReport}
        />
      )}
    </div>
  );
}